import { Main } from '../../features/ui/main';
import { getBackendApi, PostDto } from '../../features/shared/api';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { Button } from '../../features/ui/form';
import { useI18n } from '../../features/i18n';
import { Post } from '../../features/blog/components';
import { useEffect, useState } from 'react';
import { useUser } from '../../features/user';
import {
  getDraftUrl,
  getPostUrl,
  getSeoDescription,
  getSeoImage,
} from '../../features/blog/helpers';
import absoluteUrl from 'next-absolute-url';
import styles from './styles.module.scss';
import { useDraft, useViewPost } from '../../features/blog/queries';

export interface Props {
  url: string;
  post: PostDto;
}

export const getServerSideProps: GetServerSideProps<
  Props,
  { slug: string }
> = async (context) => {
  const api = getBackendApi();
  const { origin } = absoluteUrl(context.req);
  const id = (context.params?.slug || '').split('-').pop();

  if (!id) {
    return {
      notFound: true,
    };
  }

  try {
    const res = await api.blog.findPostById(id);
    const post = res.data;

    const url =
      origin +
      getPostUrl({
        id: post.id,
        slug: post.slug || undefined,
      });

    return {
      props: {
        post,
        url,
      },
    };
  } catch (e) {
    if (e instanceof Response && [404, 401].indexOf(e.status) !== -1) {
      return {
        notFound: true,
      };
    }

    throw e;
  }
};

export default function PostPage(props: Props) {
  const { post, url } = props;
  const [viewed, setViewed] = useState(false);

  const user = useUser();
  const draft = useDraft({ id: post.id });
  const viewPost = useViewPost();
  const { i18n } = useI18n();

  useEffect(() => {
    if (!viewed) {
      viewPost.mutate({ id: post.id });
      setViewed(true);
    }
  }, [viewPost, viewed, post.id]);

  const darftUrl = getDraftUrl({
    id: post.id,
    edit: true,
  });

  const publishedAt = new Date(post.publishedAt);
  const description = getSeoDescription(post.content);
  const image = getSeoImage(post.content);

  return (
    <>
      <Head>
        <title>{post.title}</title>
        {post.title && <meta property="og:title" content={post.title} />}
        {description && <meta name="description" content={description} />}
        {description && <meta name="og:description" content={description} />}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        {image && <meta property="og:image" content={image} />}
        <meta property="article:author" content="lsndr" />
        <meta property="article:published_time" content={post.publishedAt} />
        <meta property="article:modified_time”" content={post.updatedAt} />
        <link rel="canonical" href={url} />
      </Head>
      <Main
        className={styles['page']}
        rightSidebar={
          user.data && (
            <Button type={'link'} href={darftUrl} block={true}>
              {!draft.data?.published
                ? i18n('continueDraftEditing')
                : i18n('editDraft')}
            </Button>
          )
        }
      >
        <Post
          date={publishedAt}
          title={post.title || undefined}
          content={post.content}
          tags={post.tags}
        />
      </Main>
    </>
  );
}
